import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, inject, viewChild } from "@angular/core";
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { MutexService } from "common/concurrency/mutex.service";
import { Button } from "common/data/data-view.component";
import { FormService } from "common/form/form.service";
import { NewDialogComponent } from "common/newdialog/dialog.component";
import { FilesAuthEmbeddableComponent } from "files/auth/files-auth-embeddable.component";
import { FilesOperation } from "files/files-operation";
import { FileService } from "files/files.service";
import { EMPTY, Observable, Subject } from "rxjs";
import { catchError, map, takeUntil, tap } from "rxjs/operators";
import { FilesNotificationsService } from "files/notifications/filter-role/files-notifications.service";
import {
	FilesNotificationsComponent,
	NotificationInformation,
} from "files/notifications/filter-role/files-notifications.component";
import { DialogHeaderComponent } from "common/newdialog/dialog-header.component";
import { AsyncPipe, NgClass } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { EditAttributeComponent } from "common/attribute/edit-attribute.component";
import { PopupFooterComponent } from "common/popup/popup-footer.component";
import { PopupContentComponent } from "common/popup/popup-content.component";
import { FilesLoadingErrorEmbeddableComponent } from "files/error/stages-loading-error-embeddable.component";
import { FilesOAuthEmbeddableConfirmComponent } from "files/auth/files-oauth-embeddable-confirm.component";

type FileState = stages.file.FileState;
type FileStateMetaData = stages.file.FileStateMetadata;
type FileStateDataAndInfo = DataAndInfo<FileState, FileStateMetaData>;

@Component({
	selector: "stages-files-set-state",
	templateUrl: "./files-set-state.component.html",
	styleUrls: ["files-set-state.component.scss"],
	imports: [
		NewDialogComponent,
		DialogHeaderComponent,
		TranslateModule,
		ReactiveFormsModule,
		EditAttributeComponent,
		FilesNotificationsComponent,
		PopupFooterComponent,
		NgClass,
		PopupContentComponent,
		FilesLoadingErrorEmbeddableComponent,
		FilesAuthEmbeddableComponent,
		FilesOAuthEmbeddableConfirmComponent,
		AsyncPipe,
	],
})
export class FilesSetStateComponent implements OnInit, OnDestroy {
	private route = inject(ActivatedRoute);
	private filesService = inject(FileService);
	private mutexService = inject(MutexService);
	private formService = inject(FormService);
	private changeDetector = inject(ChangeDetectorRef);
	private filesNotificationsService = inject(FilesNotificationsService);

	fileStateDataAndInfo$!: Observable<FileStateDataAndInfo>;
	form!: UntypedFormGroup;
	state!: UntypedFormControl;
	comment!: UntypedFormControl;
	credentialsValid = true;
	oAuthUrl?: string;
	cmsTypeMessageKey?: string;

	readonly dialog = viewChild.required<NewDialogComponent>("dialog");

	readonly authComponent = viewChild.required<FilesAuthEmbeddableComponent>("auth");

	readonly formElement = viewChild.required<ElementRef>("formElement");

	private destroy$ = new Subject<boolean>();
	loadingError$ = new Subject<HttpErrorResponse>();

	buttons: Button[] = [];

	notifyUsers: boolean = false;
	notificationInformation?: NotificationInformation;

	ngOnInit(): void {
		this.initFileStateDataAndInfo();

		this.buttons = [
			{
				class: "button sm cancel",
				translate: "cancel",
				click: () => {
					this.close();
				},
				visible: () => true,
				disabled: () => {
					return false;
				},
			},
		];
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	private initFileStateDataAndInfo(): void {
		this.fileStateDataAndInfo$ = this.filesService
			.getStateAndInfo(
				this.route.snapshot.paramMap.get("workspaceId")!,
				this.route.snapshot.paramMap.get("elementType")!,
				this.route.snapshot.paramMap.get("elementId")!,
				this.route.snapshot.paramMap.get("processVersion")!,
				this.route.snapshot.paramMap.get("fileId")!,
			)
			.pipe(takeUntil(this.destroy$))
			.pipe(
				catchError((e: unknown) => {
					if (e instanceof HttpErrorResponse) {
						if (e.status === 901) {
							this.cmsTypeMessageKey = e.error.cmsTypeMessageKey;
							this.credentialsValid = false;
							if (e.error.authInfo.authType === "EXTERNAL_LINK") {
								this.oAuthUrl = e.error.authInfo.url;
							}
							this.changeDetector.detectChanges();
							if (this.oAuthUrl) {
								localStorage.setItem("cmOpName", FilesOperation.SET_STATE.toString());
								localStorage.setItem("cmFileId", this.route.snapshot.paramMap.get("fileId")!);
								localStorage.setItem("cmElementId", this.route.snapshot.paramMap.get("elementId")!);
								localStorage.setItem("cmElementType", this.route.snapshot.paramMap.get("elementType")!);
							} else {
								this.authComponent()
									.resultSubject.pipe(takeUntil(this.destroy$))
									.subscribe((result) => {
										if (result === "cancel") {
											this.dialog().close();
										} else {
											this.credentialsValid = true;
											this.initFileStateDataAndInfo();
										}
									});
							}
						}
						this.loadingError$.next(e);
						return EMPTY;
					}
					throw e;
				}),
			)
			.pipe(
				map((fileStateDataAndInfo) => {
					if (fileStateDataAndInfo.metadata.successorStates.length > 0) {
						fileStateDataAndInfo.data.state = fileStateDataAndInfo.metadata.successorStates[0].id;
						fileStateDataAndInfo.data.comment = null;
					}
					return fileStateDataAndInfo;
				}),
			)
			.pipe(
				tap((fileStateDataAndInfo) => {
					const createForm = this.form === undefined;
					const tempState = new UntypedFormControl(fileStateDataAndInfo.data.state);
					const tempComment = new UntypedFormControl(fileStateDataAndInfo.data.comment);
					this.state = this.state && tempState.value === this.state.value ? this.state : tempState;
					this.comment = this.comment && tempComment.value === this.comment.value ? this.comment : tempComment;
					if (createForm || tempState.value !== this.state.value || tempComment.value !== this.comment.value)
						this.form = new UntypedFormGroup({
							state: this.state,
							comment: this.comment,
						});
				}),
			);
	}

	submit(fileStateDataAndInfo: FileStateDataAndInfo): void {
		this.formService.markFormGroupTouched(this.form);
		if (this.form.invalid) {
			this.formService.scrollToFirstInvalidElement(this.formElement());
		} else {
			const outgoingAttributes = fileStateDataAndInfo.data.attributes.map((attribute) => {
				const attributeFormControl = this.form.controls[attribute.typeIdent];
				attribute.value = attributeFormControl ? attributeFormControl.value : null;
				return attribute;
			});

			this.mutexService
				.invokeWithResult(
					"saveState" +
						this.route.snapshot.paramMap.get("elementType")! +
						this.route.snapshot.paramMap.get("elementId")!,
					async () =>
						this.filesService.saveState(
							this.route,
							this.route.snapshot.paramMap.get("fileId")!,
							outgoingAttributes,
							this.route.snapshot.paramMap.get("workspaceId")!,
							this.route.snapshot.paramMap.get("processVersion")!,
						),
				)
				.then(() => {
					this.dialog().close();
					if (this.notificationInformation?.notifyUsers) {
						this.filesNotificationsService.sendNotificationsSetState(
							this.route.snapshot.paramMap.get("workspaceId")!,
							this.route.snapshot.paramMap.get("elementType")!,
							this.route.snapshot.paramMap.get("elementId")!,
							this.route.snapshot.paramMap.get("fileId")!,
							this.route.snapshot.paramMap.get("processVersion")!,
							this.notificationInformation,
						);
					}
				});
		}
	}

	close = (): void => {
		const dialog = this.dialog();
		if (dialog) {
			dialog.close();
		}
	};

	showNotificationOptions(): void {
		this.notifyUsers = !this.notifyUsers;
		if (this.notifyUsers) {
			this.fileStateDataAndInfo$
				.pipe(takeUntil(this.destroy$ || !this.notifyUsers))
				.subscribe((fileStateDataAndInfo) => {
					this.hasFormChanges(fileStateDataAndInfo);
				});
		}
	}

	hasFormChanges(fileStateDataAndInfo: FileStateDataAndInfo): void {
		const stateAttributeIdentifier = fileStateDataAndInfo.data.stateAttributeIdentifier;

		const lifecycleStateType = fileStateDataAndInfo.data.attributes.find(
			(type) => type.typeIdent === stateAttributeIdentifier,
		);
		if (lifecycleStateType !== undefined) {
			this.filesNotificationsService.setLifecycleStateType(lifecycleStateType.value);
		}

		this.filesNotificationsService.setLifecycleTypeIdent(fileStateDataAndInfo.metadata.lifecycleIdent);

		if (stateAttributeIdentifier !== null && this.form.value[stateAttributeIdentifier]) {
			this.filesNotificationsService.setNextLifecycleState(this.form.value[stateAttributeIdentifier]);
		} else {
			this.filesNotificationsService.setNextLifecycleState(this.form.value.state);
		}
	}

	setNotificationInformation(notificationInformation: NotificationInformation): void {
		this.notificationInformation = notificationInformation;
	}

	isNotifyUsersAllowed(fileStateDataAndInfo: FileStateDataAndInfo): boolean {
		return fileStateDataAndInfo.actions.SendFileNotifications;
	}
}
